import { resultAverageSpeed } from '../../models/result-table.models';
import { RegisteredStarComponent } from '../../components/registered-star/registered-star.component';
import { Component, ChangeDetectionStrategy, inject, signal, computed, input, output, effect, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RaceService } from '../../services/race.service';
import { ResultService } from '../../services/result.service';
import { HumanService } from '../../services/human.service';
import { DogService } from '../../services/dog.service';
import { AuthService } from '../../services/auth.service';
import { TranslationService } from '../../services/translation.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { DisplayValuePipe } from '../../pipes/display-value.pipe';
import { DistancePipe } from '../../pipes/distance.pipe';
import { ResultStatusPipe } from '../../pipes/result-status.pipe';
import { BreedNamePipe } from '../../pipes/breed-name.pipe';
import { RunnerStatisticsComponent } from '../../components/runner-statistics/runner-statistics.component';
import { summarizeEvent } from '../../models/event-summary.models';
import { ImageGalleryComponent } from '../../components/image-gallery/image-gallery.component';
import { EventMapComponent, EventMapPoint } from '../../components/event-map/event-map.component';
import { RouterModule } from '@angular/router';
import { BackNavigationDirective } from '../../directives/back-navigation.directive';
import { BrandLogoComponent } from '../../components/brand-logo/brand-logo.component';
import { RegionalThemeService } from '../../services/regional-theme.service';
import { BrandedLoaderComponent } from '../../components/branded-loader/branded-loader.component';
import { Event as RaceEvent, Race, Result } from '../../models/data.models';
import { ClubPickerComponent, ClubSelection } from '../../components/club-picker/club-picker.component';
import { calculateCategoryStandings, normalizeStandingCategory } from '../../models/category-standings.models';
import { withCategoryStanding } from '../../models/podium.models';
import { normalizeBreedName } from '../../models/dog-breed.models';
import {
  RESULT_DISCIPLINES,
  ResultDiscipline,
  normalizeResultDiscipline,
} from '../../models/discipline.models';

interface QuickFilter {
  type: 'discipline';
  value: string;
  labelKey: string;
  label: string;
  distanceKm?: number;
}

@Component({
  selector: 'app-race-detail',
  standalone: true,
  imports: [RegisteredStarComponent, CommonModule, TranslatePipe, DisplayValuePipe, DistancePipe, ResultStatusPipe, BreedNamePipe, RunnerStatisticsComponent, ImageGalleryComponent, EventMapComponent, RouterModule, BackNavigationDirective, BrandLogoComponent, BrandedLoaderComponent, ClubPickerComponent],
  templateUrl: './race-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RaceDetailComponent implements OnDestroy {
  raceService = inject(RaceService);
  resultService = inject(ResultService);
  humanService = inject(HumanService);
  dogService = inject(DogService);
  authService = inject(AuthService);
  translationService = inject(TranslationService);
  readonly regionalTheme = inject(RegionalThemeService);

  raceId = input.required<string>({ alias: 'id' });

  getFlagEmoji = this.humanService.getFlagEmoji;
  // Use bound method or lambda to preserve 'this' if needed inside template or just expose service wrapper
  getCategory = (human: any) => this.humanService.getCategory(human);

  // State
  event = signal<RaceEvent | null>(null);
  results = signal<Result[]>([]);
  isLoading = signal(true);

  readonly hasStructuredLocation = computed(() => {
    const currentEvent = this.event();
    return !!currentEvent && (
      !!currentEvent.city ||
      !!currentEvent.departmentCode ||
      !!currentEvent.address ||
      this.hasCoordinates(currentEvent)
    );
  });

  readonly locationPrecisionLabel = computed(() => {
    const currentEvent = this.event();
    const precision = currentEvent?.geoPrecision;
    if (precision === 'venue') {
      return currentEvent?.geoSource === 'manual:admin'
        ? this.translationService.translate('common.manualGps')
        : this.translationService.translate('common.sourceGps');
    }
    if (precision === 'address') return this.translationService.translate('common.preciseAddress');
    if (precision === 'postal_code' || precision === 'city_center') return this.translationService.translate('common.approximatePosition');
    return null;
  });

  readonly isExactLocation = computed(() => {
    const precision = this.event()?.geoPrecision;
    return precision === 'venue' || precision === 'address';
  });

  readonly mapPoints = computed<EventMapPoint[]>(() => {
    const currentEvent = this.event();
    if (!currentEvent || !this.hasCoordinates(currentEvent)) return [];

    return [{
      id: currentEvent.id,
      name: currentEvent.name,
      city: currentEvent.city || currentEvent.location,
      departmentCode: currentEvent.departmentCode,
      latitude: currentEvent.latitude,
      longitude: currentEvent.longitude,
      precision: currentEvent.geoPrecision,
      state: this.getEventMapState(currentEvent),
    }];
  });

  readonly externalMapUrl = computed(() => {
    const currentEvent = this.event();
    if (!currentEvent || !this.hasCoordinates(currentEvent)) return null;
    const { latitude, longitude } = currentEvent;
    return `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=15/${latitude}/${longitude}`;
  });

  readonly directionsUrl = computed(() => {
    const currentEvent = this.event();
    if (!currentEvent || !this.isExactLocation()) return null;

    const destination = this.hasCoordinates(currentEvent)
      ? `${currentEvent.latitude},${currentEvent.longitude}`
      : currentEvent.address?.trim();
    if (!destination) return null;

    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
  });

  // Filter signals
  genderFilter = signal<'ALL' | 'H' | 'F'>('ALL');
  disciplineFilter = signal<'ALL' | ResultDiscipline>('ALL');
  dogBreedFilter = signal<string>('ALL');
  categoryFilter = signal<string>('ALL');
  clubFilter = signal<string>('ALL');
  readonly disciplineOptions = RESULT_DISCIPLINES;

  isAdmin = this.authService.isAdmin;
  isEditMode = signal(false);
  showSourceDebug = signal(false);

  categoryLabels: Record<string, string> = {
    'S': 'SENIOR',
    'J': 'JUNIOR',
    'V1': 'VÉTÉRAN 1',
    'V2': 'VÉTÉRAN 2',
    'V3': 'VÉTÉRAN 3'
  };

  /**
   * Normalizes strings for matching (Clubs, Breeds, etc.)
   * Removes leading articles, punctuation, and extra spaces.
   */
  normalizeString(str: string | null | undefined): string {
    if (!str) return '';
    // 1. Remove accents (diacritics) and convert to uppercase
    let normalized = str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().trim();
    
    // 2. Remove common French leading articles (handle L', L , etc.)
    // Matches "LES ", "LE ", "LA ", "L'", "D'", "L ", "D " at the start
    normalized = normalized.replace(/^(LES |LE |LA |L['’]|D['’]|L\s+|D\s+)/g, '');
    
    // 3. Remove ALL non-alphanumeric characters (including all spaces and punctuation)
    // This merges "CANI CLUB" and "CANICLUB"
    normalized = normalized.replace(/[^A-Z0-9]/g, '');
    
    return normalized;
  }

  constructor() {
    // Effect to reload details when raceId changes
    effect(() => {
      const id = this.raceId();
      if (id) {
        this.loadRaceDetails(id);
      }
    });

    // Effect to enforce discipline selection
    effect(() => {
        const filters = this.quickFilters().filter(f => f.type === 'discipline');
        const current = this.disciplineFilter();
        
        if (filters.length > 0) {
            // Strict rule: never mix disciplines. Default to first if ALL or invalid.
            if (current === 'ALL' || !filters.some(f => f.value === current)) {
                this.disciplineFilter.set(filters[0].value as ResultDiscipline);
            }
        }
    });

    effect(() => {
      const currentEvent = this.event();
      if (currentEvent?.departmentCode) {
        this.regionalTheme.setContextDepartment(currentEvent.departmentCode);
      } else {
        this.regionalTheme.setContextAdministrativeRegion(currentEvent?.region);
      }
    });
  }

  ngOnDestroy(): void {
    this.regionalTheme.clearContext();
  }

  private loadVersion = 0;

  async loadRaceDetails(id: string) {
    // Navigating between events reuses this component: only the latest request may fill it.
    const version = ++this.loadVersion;
    this.isLoading.set(true);
    try {
      const eventId = await this.raceService.resolveEventId(id);
      if (version !== this.loadVersion) return;
      if (!eventId) {
        this.event.set(null);
        this.results.set([]);
        return;
      }
      const [eventData, resultsData] = await Promise.all([
        this.raceService.getEvent(eventId),
        this.resultService.getResultsByEvent(eventId),
      ]);
      if (version !== this.loadVersion) return;
      this.event.set(eventData);
      this.results.set(resultsData);
    } catch (err) {
      console.error('Error loading race details:', err);
    } finally {
      if (version === this.loadVersion) this.isLoading.set(false);
    }
  }

  // Computed results
  raceResults = computed(() =>
    this.results()
      .slice() // Copy before sort just in case
      .sort((a, b) => {
        const aHasTime = (a.timeSeconds ?? 0) > 0;
        const bHasTime = (b.timeSeconds ?? 0) > 0;
        if (aHasTime && bHasTime) return a.timeSeconds - b.timeSeconds;
        if (aHasTime !== bHasTime) return aHasTime ? -1 : 1;
        return (a.position ?? Number.MAX_SAFE_INTEGER) -
          (b.position ?? Number.MAX_SAFE_INTEGER);
      })
  );

  getDisplayCategory(result: Result): string {
    const cat = result.category || (result.team ? this.humanService.getCategory(result.team.human) : 'Unknown');
    // Remove anything in parentheses (like ages) but keep the code (VHV1, CPHS, etc.)
    return normalizeStandingCategory(cat);
  }

  getFilterCategory(result: Result): string {
    const rawCat = this.getDisplayCategory(result).toUpperCase();
    
    // Mapping prefixes: V = VTT, CP = Trott, C = Canicross
    let cat = rawCat;
    if (cat.startsWith('CP')) cat = cat.substring(2);
    else if (cat.startsWith('V') && !cat.startsWith('V1') && !cat.startsWith('V2') && !cat.startsWith('V3')) cat = cat.substring(1);
    else if (cat.startsWith('C') && cat.length > 1) {
      const secondChar = cat.charAt(1);
      if (secondChar === 'H' || secondChar === 'F' || secondChar === 'S' || secondChar === 'J') {
        cat = cat.substring(1);
      }
    }

    if (cat === 'SEH' || cat === 'SENIOR H') return 'HS';
    if (cat === 'SEF' || cat === 'SENIOR F') return 'FS';
    
    return cat.trim();
  }

  getAgeCode(result: Result): string {
    const filterCat = this.getFilterCategory(result);
    // Returns the part after H or F (e.g., HS -> S, FV1 -> V1)
    if (filterCat.startsWith('H') || filterCat.startsWith('F')) {
        return filterCat.substring(1);
    }
    return filterCat;
  }

  getGenderCode(result: Result): string {
    const filterCat = this.getFilterCategory(result);
    if (filterCat.startsWith('H')) return 'H';
    if (filterCat.startsWith('F')) return 'F';
    return 'ALL';
  }

  getEffectiveDiscipline(result: Result): ResultDiscipline | null {
    return normalizeResultDiscipline(result.discipline, result.race?.name, result.category);
  }

  private resultsByCategory = computed(() => calculateCategoryStandings(this.raceResults().map(result => ({
    ...result, gender: result.team?.human?.gender,
  }))));

  quickFilters = computed<QuickFilter[]>(() => {
    const evt = this.event();
    const races = evt?.races ?? [];
    const available = new Set<ResultDiscipline>();

    races.forEach(race => {
      const discipline = normalizeResultDiscipline(race.discipline, race.name);
      if (discipline) available.add(discipline);
    });
    this.raceResults().forEach(result => {
      const discipline = this.getEffectiveDiscipline(result);
      if (discipline) available.add(discipline);
    });

    return RESULT_DISCIPLINES.filter(definition => available.has(definition.code)).map(
      definition => {
        const distances = new Set(
          races
            .filter(
              race =>
                normalizeResultDiscipline(race.discipline, race.name) === definition.code &&
                race.distanceKm > 0,
            )
            .map(race => race.distanceKm),
        );
        return {
          type: 'discipline' as const,
          value: definition.code,
          labelKey: definition.labelKey,
          label: definition.label,
          distanceKm: distances.size === 1 ? [...distances][0] : undefined,
        };
      },
    );
  });

  /** Results published from an organiser's PDF, before the federation validates them. */
  readonly hasUnofficialResults = computed(() =>
    this.results().some(result => result.sourceMetadata?.['validationStatus'] === 'unofficial'));

  /** Statistics of every result of the event, independent of the leaderboard filters. */
  readonly eventSummary = computed(() => summarizeEvent(this.results().map(result => ({
    humanId: result.team?.human?.id,
    dogId: result.team?.dog?.id,
    statusCode: result.statusCode,
  }))));

  readonly eventDisciplines = computed(() => {
    const total = this.results().length;
    return this.stats().disciplineStats.map(({ name, count }) => ({
      code: name,
      label: this.getDisciplineDisplayName(name),
      count,
      percent: total ? count / total * 100 : 0,
    }));
  });

  /** Races runners can describe (elevation, distance, difficulty, waymarking). */
  readonly contributionRaces = computed(() => (this.event()?.races ?? []).map(race => ({
    id: race.id,
    name: race.name,
    distance: race.distanceKm ? `${race.distanceKm} KM` : '-',
  })));

  formatPercent(value: number): string {
    return new Intl.NumberFormat(this.translationService.currentLang(), { maximumFractionDigits: 1 }).format(value);
  }

  getDisciplineDisplayName(code: string): string {
    const definition = RESULT_DISCIPLINES.find(discipline => discipline.code === code);
    return definition ? this.translationService.translate(definition.labelKey) : code.toUpperCase();
  }

  getRaceDisciplineValue(race: { discipline?: string; name: string }): string {
    return normalizeResultDiscipline(race.discipline, race.name) ?? race.discipline ?? '';
  }

  dogBreeds = computed(() => {
    const breeds = this.raceResults()
      .map(r => r.team?.dog?.breed)
      .filter((breed): breed is string => !!breed)
      .map(breed => normalizeBreedName(breed));
    return ['ALL', ...Array.from(new Set(breeds)).sort((a, b) => a.localeCompare(b))];
  });

  categories = computed(() => {
    const allowedAgeCodes = ['S', 'J', 'V1', 'V2', 'V3'];
    
    const codes = Array.from(new Set(
      this.raceResults()
        .map(r => this.getAgeCode(r))
        .filter(c => allowedAgeCodes.includes(c))
    ));

    const order = ['B', 'M', 'C', 'J', 'S', 'V1', 'V2', 'V3'];
    return ['ALL', ...codes.sort((a, b) => order.indexOf(a) - order.indexOf(b))];
  });

  clubs = computed(() => {
    const allClubs = this.raceResults()
      .map(r => r.team!.human.club)
      .filter((c): c is string => !!c);
    
    // Group by normalized name to avoid duplicates
    const normalizedMap = new Map<string, string>();
    allClubs.forEach(raw => {
      const norm = this.normalizeString(raw);
      if (!normalizedMap.has(norm)) {
        normalizedMap.set(norm, raw.toUpperCase().trim());
      }
    });

    return ['ALL', ...Array.from(normalizedMap.values()).sort()];
  });

  filteredLeaderboard = computed(() => {
    const gender = this.genderFilter();
    const discipline = this.disciplineFilter();
    const dogBreed = this.dogBreedFilter();
    const category = this.categoryFilter(); // This is now AgeCode (S, J, V1...)
    const club = this.clubFilter();
    const categoryMap = this.resultsByCategory();

    const filtered = this.raceResults().filter(result => {
      const team = result.team;
      if (!team) return false;
      const human = team.human;
      const dog = team.dog;

      const filterGender = this.getGenderCode(result);
      const disciplineMatch =
        discipline === 'ALL' || this.getEffectiveDiscipline(result) === discipline;
      const dogBreedMatch = dogBreed === 'ALL' || (!!dog?.breed && normalizeBreedName(dog.breed) === dogBreed);
      
      const genderMatch = gender === 'ALL' || filterGender === gender;
      
      const filterAge = this.getAgeCode(result);
      const categoryMatch = category === 'ALL' || (filterAge === category.toUpperCase());
      
      const clubMatch = club === 'ALL' || (this.normalizeString(human?.club) === this.normalizeString(club));

      return genderMatch && disciplineMatch && dogBreedMatch && categoryMatch && clubMatch;
    });

    return filtered.map(result => withCategoryStanding(result, categoryMap[result.id]));
  });

  winnerTimeForView = computed(() => {
    const leaderboard = this.filteredLeaderboard();
    return leaderboard.find(r => r.timeSeconds > 0)?.timeSeconds || 0;
  });

  stats = computed(() => {
    const results = this.results();

    // 1. Top 3 Clubs (Normalized)
    const clubMap = new Map<string, { name: string, count: number }>();
    results.forEach(r => {
      const rawClub = r.team?.human?.club;
      if (rawClub) {
        const norm = this.normalizeString(rawClub);
        const existing = clubMap.get(norm);
        if (existing) {
          existing.count++;
        } else {
          clubMap.set(norm, { name: rawClub.toUpperCase().trim(), count: 1 });
        }
      }
    });
    const topClubs = Array.from(clubMap.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    // 2. Top 3 Breeds
    const breedMap = new Map<string, number>();
    results.forEach(r => {
      const breed = r.team?.dog?.breed ? normalizeBreedName(r.team.dog.breed) : undefined;
      if (breed) {
        breedMap.set(breed, (breedMap.get(breed) || 0) + 1);
      }
    });
    const topBreeds = Array.from(breedMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);

    // 3. Disciplines
    const disciplineMap = new Map<string, number>();
    results.forEach(r => {
      const discipline = this.getEffectiveDiscipline(r);
      if (discipline) {
        disciplineMap.set(discipline, (disciplineMap.get(discipline) || 0) + 1);
      }
    });
    const disciplineStats = Array.from(disciplineMap.entries())
      .map(([name, count]) => ({ name, count }))
      .sort(
        (a, b) =>
          RESULT_DISCIPLINES.findIndex(discipline => discipline.code === a.name) -
          RESULT_DISCIPLINES.findIndex(discipline => discipline.code === b.name),
      );

    return {
      totalParticipants: results.length,
      topClubs,
      topBreeds,
      disciplineStats
    };
  });

  isFilterActive(filter: QuickFilter): boolean {
    return this.disciplineFilter() === filter.value;
  }

  onQuickFilterChange(filter: QuickFilter) {
    // Disciplines are never mixed in one ranking (see the constructor effect), so a quick filter
    // selects its discipline instead of toggling back to "all".
    this.disciplineFilter.set(filter.value as ResultDiscipline);
  }

  onGenderChange(event: Event) {
    this.genderFilter.set((event.target as HTMLSelectElement).value as 'ALL' | 'H' | 'F');
  }

  onCategoryChange(event: Event) {
    this.categoryFilter.set((event.target as HTMLSelectElement).value);
  }

  onDisciplineSelectChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value as 'ALL' | ResultDiscipline;
    this.disciplineFilter.set(value);
  }

  onDogBreedChange(event: Event) {
    this.dogBreedFilter.set((event.target as HTMLSelectElement).value);
  }


  onClubChange(event: Event) {
    this.clubFilter.set((event.target as HTMLSelectElement).value);
  }

  formatEventLocation(currentEvent: RaceEvent): string {
    const city = currentEvent.city?.trim();
    const fallbackLocation = currentEvent.location?.trim();
    const departmentCode = currentEvent.departmentCode?.trim();
    const location = city || fallbackLocation;

    if (location && departmentCode && !location.includes(`(${departmentCode})`)) {
      return `${location} (${departmentCode})`;
    }
    if (location) return location;
    if (departmentCode) return `Département ${departmentCode}`;
    return this.translationService.translate('races.locationPending');
  }

  formatDate(isoString: string): string {
    if (!isoString) return this.translationService.translate('dataStats.unknownDate');
    const date = new Date(isoString);
    if (isNaN(date.getTime()) || date.getFullYear() < 1990) {
      return this.translationService.translate('dataStats.unknownDate');
    }
    const locale = this.translationService.currentLang() === 'fr' ? 'fr-FR' : 'en-US';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  private hasCoordinates(currentEvent: RaceEvent): currentEvent is RaceEvent & { latitude: number; longitude: number } {
    return Number.isFinite(currentEvent.latitude) && Number.isFinite(currentEvent.longitude);
  }

  private getEventMapState(currentEvent: RaceEvent): EventMapPoint['state'] {
    const datePart = currentEvent.date?.slice(0, 10);
    const dateMatch = datePart?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const eventTimestamp = dateMatch
      ? new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3])).getTime()
      : new Date(currentEvent.date).getTime();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (Number.isFinite(eventTimestamp) && eventTimestamp >= today.getTime()) return 'upcoming';
    return this.results().length > 0 ? 'past' : 'pending';
  }

  formatTime(totalSeconds: number | null | undefined): string {
    if (totalSeconds == null || totalSeconds <= 0) return '—';
    // Round once to whole centiseconds so that e.g. 125.996 s carries over to 2m 06s.
    const totalCentiseconds = Math.round(totalSeconds * 100);
    const centiseconds = totalCentiseconds % 100;
    const wholeSeconds = Math.floor(totalCentiseconds / 100);
    const hours = Math.floor(wholeSeconds / 3600);
    const minutes = Math.floor((wholeSeconds % 3600) / 60);
    const seconds = wholeSeconds % 60;

    const pad = (num: number) => num.toString().padStart(2, '0');

    let timeStr = '';
    if (hours > 0) {
      timeStr = `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
    } else if (minutes > 0) {
      timeStr = `${pad(minutes)}m ${pad(seconds)}s`;
    } else {
      timeStr = `${pad(seconds)}s`;
    }

    if (centiseconds > 0) timeStr += ` ${pad(centiseconds)}`;

    return timeStr;
  }

  formatTimeGap(seconds: number, winnerSeconds: number, previousSeconds?: number): string {
    if (winnerSeconds <= 0 || seconds <= 0 || seconds === winnerSeconds) {
      return '—';
    }
    
    const gapWinner = seconds - winnerSeconds;
    let result = `+${this.formatTime(gapWinner)}`;
    
    // Show gap to previous if it's different from the gap to winner 
    // OR if the user explicitly wants to see the step-by-step gaps.
    if (previousSeconds !== undefined && previousSeconds > 0 && seconds !== previousSeconds) {
      const gapPrev = seconds - previousSeconds;
      // We always show it if it's not the same as winner gap to make it clearer
      if (previousSeconds !== winnerSeconds) {
        result += ` (+${this.formatTime(gapPrev)})`;
      }
    }
    
    return result;
  }

  getWinnerTime(result: Result): number {
      const category = this.getDisplayCategory(result).toUpperCase();
      const discipline = (result.discipline || '').toUpperCase();
      
      const results = this.raceResults().filter(r => 
          this.getDisplayCategory(r).toUpperCase() === category &&
          (r.discipline || '').toUpperCase() === discipline &&
          r.timeSeconds > 0
      );
      if (results.length === 0) return 0;
      return Math.min(...results.map(r => r.timeSeconds));
  }

  resultSpeed(result: Result): string {
    const distance = result.race?.distanceKm ?? this.event()?.races?.find(race => race.id === result.raceId)?.distanceKm;
    const speed = resultAverageSpeed(distance, result.timeSeconds, result.statusCode, result.averageSpeedKmph);
    return speed == null ? '—' : this.formatSpeed(speed);
  }

  formatSpeed(speed: number, discipline?: string): string {
      if (!speed) return '-';
      const locale = this.translationService.currentLang() === 'fr' ? 'fr-FR' : 'en-US';
      return speed.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' km/h';
  }

  calculateAverageSpeed(timeSeconds: number, eventId: string, discipline: string): number {
    if (timeSeconds === 0) return 0;
    const evt = this.event();
    if (!evt || !evt.races) return 0;

    const race = evt.races.find(r => r.discipline === discipline);
    // Use distanceKm as confirmed by lint error
    if (!race || !race.distanceKm) return 0;

    return (race.distanceKm / (timeSeconds / 3600));
  }

  formatGender(gender: 'Male' | 'Female' | 'Other'): string {
    switch (gender) {
      case 'Male': return 'H';
      case 'Female': return 'F';
      default: return 'A';
    }
  }

  toggleEditMode() {
    this.isEditMode.update(v => !v);
  }

  toggleSourceDebug() {
    if (!this.isAdmin()) return;
    this.showSourceDebug.update(value => !value);
  }

  getResultSourceLabel(result: Result): string {
    return result.sourceType || this.translationService.translate('admin.resultatsCourse.sourceUnknown');
  }

  getResultSourceTitle(result: Result): string {
    const details = result.sourceMetadata
      ? `\n${JSON.stringify(result.sourceMetadata, null, 2)}`
      : '';
    return `${result.sourceRecordKey || this.translationService.translate('admin.resultatsCourse.sourceKeyMissing')}${details}`;
  }

  /** Admin edit mode: an edit that fails is reported, then the page reloads the stored values. */
  private async applyAdminEdit(save: () => Promise<void>): Promise<void> {
    try {
      await save();
    } catch (err) {
      console.error('Race detail edit failed:', err);
      alert(this.translationService.translate('raceDetail.editError'));
    }
    await this.loadRaceDetails(this.raceId());
  }

  async updateResultField(resultId: string, field: 'timeSeconds' | 'position' | 'bibNumber' | 'category', event: Event) {
    const value = (event.target as HTMLInputElement).value.trim();
    const updates: Partial<Result> = {};
    if (field === 'timeSeconds') {
      const seconds = this.parseTimeToSeconds(value);
      if (seconds <= 0) {
        alert(this.translationService.translate('raceDetail.invalidTime'));
        return;
      }
      updates.timeSeconds = seconds;
    } else if (field === 'position') {
      const position = value ? Number(value) : undefined;
      if (position !== undefined && (!Number.isInteger(position) || position <= 0)) {
        alert(this.translationService.translate('raceDetail.invalidPosition'));
        return;
      }
      updates.position = position;
    } else if (field === 'bibNumber') {
      updates.bibNumber = value || undefined;
    } else {
      updates.category = value || undefined;
    }
    await this.applyAdminEdit(() => this.resultService.updateResult(resultId, updates));
  }

  /** Renames a runner or a dog: the change applies to all their results, as they are one entity. */
  async updateHumanName(humanId: string, event: Event) {
    const name = (event.target as HTMLInputElement).value.trim();
    if (!name) return;
    await this.applyAdminEdit(() => this.humanService.updateHuman(humanId, { name }));
  }

  /** Humans reference clubs by id: only a club picked from the suggestions is saved. */
  async updateHumanClub(humanId: string, selection: ClubSelection) {
    if (!selection.resolved) return;
    await this.applyAdminEdit(() => this.humanService.updateHumanClub(humanId, selection.id));
  }

  async updateDogField(dogId: string, field: 'name' | 'breed', event: Event) {
    const value = (event.target as HTMLInputElement).value.trim();
    if (field === 'name' && !value) return;
    await this.applyAdminEdit(() => this.dogService.updateDog(dogId, { [field]: value }));
  }

  async updateRaceConfig(raceId: string, field: 'discipline' | 'distanceKm', event: Event) {
    const value = (event.target as HTMLInputElement).value;
    const updates: Partial<Race> = {};
    if (field === 'distanceKm') {
      const distance = parseFloat(value.replace(',', '.'));
      if (!Number.isFinite(distance) || distance <= 0) return;
      updates.distanceKm = distance;
    } else {
      const discipline = RESULT_DISCIPLINES.find(definition => definition.code === value)?.code;
      if (!discipline) return;
      updates.discipline = discipline;
    }
    await this.applyAdminEdit(() => this.raceService.updateRace(raceId, updates));
  }

  async updateEventInfo(field: 'name' | 'location' | 'date' | 'region' | 'description', event: Event) {
    const input = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    const eventId = this.event()?.id;
    if (!eventId) return;
    const updates: Partial<RaceEvent> = { [field]: input.value };
    await this.applyAdminEdit(() => this.raceService.updateEvent(eventId, updates));
  }

  async saveEventLocation(
    cityValue: string,
    departmentValue: string,
    postalValue: string,
    addressValue: string,
    latitudeValue: string,
    longitudeValue: string,
    precisionValue: string,
  ): Promise<void> {
    const currentEvent = this.event();
    if (!currentEvent) return;

    const parseCoordinate = (value: string): number | null => {
      if (!value.trim()) return null;
      const parsed = Number(value.replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : Number.NaN;
    };
    const latitude = parseCoordinate(latitudeValue);
    const longitude = parseCoordinate(longitudeValue);
    if (Number.isNaN(latitude) || Number.isNaN(longitude) ||
      (latitude == null) !== (longitude == null) ||
      (latitude != null && (latitude < -90 || latitude > 90)) ||
      (longitude != null && (longitude < -180 || longitude > 180))) {
      alert(this.translationService.translate('common.invalidCoordinates'));
      return;
    }

    const city = cityValue.trim() || null;
    const departmentCode = departmentValue.trim().toUpperCase() || null;
    const postalCode = postalValue.trim() || null;
    const address = addressValue.trim() || null;
    const allowedPrecisions = new Set(['venue', 'address', 'postal_code', 'city_center']);
    const geoPrecision = allowedPrecisions.has(precisionValue)
      ? precisionValue as RaceEvent['geoPrecision']
      : null;
    if (departmentCode && !/^(?:\d{2,3}|2[AB])$/.test(departmentCode)) {
      alert(this.translationService.translate('common.invalidDepartment'));
      return;
    }
    if (postalCode && !/^\d{5}$/.test(postalCode)) {
      alert(this.translationService.translate('common.invalidPostalCode'));
      return;
    }
    if (geoPrecision === 'venue' && latitude == null) {
      alert(this.translationService.translate('common.gpsCoordinatesRequired'));
      return;
    }
    if (geoPrecision === 'address' && !address) {
      alert(this.translationService.translate('common.addressRequired'));
      return;
    }
    if (geoPrecision === 'postal_code' && !postalCode) {
      alert(this.translationService.translate('common.postalCodePrecisionRequired'));
      return;
    }
    if (geoPrecision === 'city_center' && !city) {
      alert(this.translationService.translate('common.cityCenterPrecisionRequired'));
      return;
    }
    const hasLocationData = !!(city || departmentCode || postalCode || address || latitude != null);
    const administrativePlaceChanged = city !== (currentEvent.city ?? null) ||
      departmentCode !== (currentEvent.departmentCode ?? null) ||
      postalCode !== (currentEvent.postalCode ?? null);

    try {
      await this.raceService.updateEvent(currentEvent.id, {
        city,
        location: city || currentEvent.location,
        departmentCode,
        postalCode,
        address,
        latitude,
        longitude,
        geoPrecision,
        geoSource: hasLocationData ? 'manual:admin' : null,
        geoConfidence: hasLocationData
          ? (geoPrecision === 'venue' || geoPrecision === 'address' ? 'high' : 'medium')
          : null,
        ...(administrativePlaceChanged ? { inseeCode: null } : {}),
      } as Partial<RaceEvent>);
      await this.loadRaceDetails(currentEvent.id);
    } catch (error) {
      console.error('Location update failed', error);
      alert(this.translationService.translate('common.locationSaveFailed'));
    }
  }

  async deleteResultRow(resultId: string) {
    if (!confirm(this.translationService.translate('common.deleteResultConfirm'))) return;
    try {
      await this.resultService.deleteResult(resultId);
      this.results.update(curr => curr.filter(r => r.id !== resultId));
    } catch (err) {
      console.error(err);
      alert(this.translationService.translate('common.deleteError'));
    }
  }

  /** Accepts hh:mm:ss, mm:ss, seconds, or the displayed "1h 02m 03s" format. */
  parseTimeToSeconds(time: string): number {
    const cleanTime = time.trim().replace(',', '.');
    const units = cleanTime.match(/^(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?\s*(?:(\d+(?:\.\d+)?)\s*s)?$/i);
    if (units && (units[1] || units[2] || units[3])) {
      return Number(units[1] ?? 0) * 3600 + Number(units[2] ?? 0) * 60 + Number(units[3] ?? 0);
    }
    if (!cleanTime) return 0;
    const parts = cleanTime.split(':').map(Number);
    if (parts.some(p => isNaN(p))) return 0;

    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    } else if (parts.length === 1) {
      return parts[0];
    }
    return 0;
  }

  // State for UI visibility
  showEmptyColumns = signal(false);

  toggleShowEmptyColumns() {
    this.showEmptyColumns.update(v => !v);
  }

  // Data presence computed signals
  hasPositionData = computed(() => this.raceResults().some(r => r.position !== null && r.position !== undefined));
  hasBibData = computed(() => this.raceResults().some(r => !!r.bibNumber));
  hasClubData = computed(() => this.raceResults().some(r => !!r.team?.human?.club));
  hasCategoryData = computed(() => this.raceResults().some(r => !!r.category));
  hasHumanData = computed(() => this.raceResults().some(r => !!r.team?.human?.name));
  hasDogData = computed(() => this.raceResults().some(r => !!r.team?.dog?.name));
  hasGenderData = computed(() => this.raceResults().some(r => !!r.team?.human?.gender)); // Always true usually
  hasBreedData = computed(() => this.raceResults().some(r => !!r.team?.dog?.breed && r.team.dog.breed !== 'Inconnu'));
  hasTimeData = computed(() => this.raceResults().some(r => !!r.timeSeconds)); 

  hasCategoryRankData = computed(() =>
    this.hasCategoryData() && (this.hasTimeData() || this.hasPositionData()),
  );
  hasSpeedData = computed(() => this.hasTimeData()); // Derived
  hasGapData = computed(() => this.hasTimeData()); // Derived

  isColumnVisible(column: string): boolean {
      if (this.showEmptyColumns()) return true;
      
      switch (column) {
          case 'position': return this.hasPositionData();
          case 'bibNumber': return this.hasBibData();
          case 'club': return this.hasClubData();
          case 'category': return this.hasCategoryData();
          case 'breed': return this.hasBreedData();
          case 'timeSeconds': return this.hasTimeData();
          case 'human': return this.hasHumanData();
        case 'dog': return this.hasDogData();
        case 'gender': return false; // Redundant with category
        case 'categoryRank': return this.hasCategoryRankData();
          case 'averageSpeed': return this.hasSpeedData();
          case 'timeGap': return this.hasGapData();
          default: return true;
      }
  }
}
